import { VaultCard } from '@/components/shared/vaultCard'
import { MINIMUM_BALANCE_THRESHOLD } from '@/config/query/queryParams'
import { routes } from '@/config/routes'
import { useRelevantDepositOptions } from '@/hooks/useRelevantDepositOptions'
import { useVaultsWithPositions } from '@/hooks/useVaultsWithPositions'
import { useBestDepositOptionsQuery } from '@/queries/bestDepositOptions.query'
import { usePositionsQuery } from '@/queries/positions.query'
import { useVaultsQuery } from '@/queries/vaults.query'
import type { VaultApyRange } from '@/types/apy'
import { cn } from '@/utils/cn'
import { useMemo } from 'react'
import { Link } from 'react-router'
import type { Address } from 'viem'
import { ArrowIcon } from '../icons/arrow'
import { IdleAssetsBanner } from './idleAssetsBanner'
import type { AssetsBalanceGroup } from './useGroupedBalances/useGroupedBalances'

interface Props {
  address: Address | undefined
  apyRange: VaultApyRange
  includeRewards: boolean
  assetsBalance?: AssetsBalanceGroup | null
  className?: string
}

export function ProposedVaultsPanel({ address, apyRange, includeRewards, assetsBalance, className }: Props) {
  const { data: vaultsQueryData } = useVaultsQuery({ initialData: undefined })
  const vaults = vaultsQueryData ?? []
  const { data: positionsList } = usePositionsQuery({ address, apyInterval: apyRange })
  const vaultsWithPositions = useVaultsWithPositions(vaults, positionsList)

  const hasIdleAssets = (assetsBalance?.length ?? 0) > 0
  const allowedAssets = useMemo(
    () => (hasIdleAssets ? assetsBalance?.map((group) => group.groupName) : undefined),
    [assetsBalance, hasIdleAssets]
  )

  const { data: depositOptions } = useBestDepositOptionsQuery({
    address,
    allowedAssets,
    minUsdAssetValueThreshold: MINIMUM_BALANCE_THRESHOLD,
    enabled: hasIdleAssets,
    apyInterval: apyRange,
    initialData: undefined,
  })

  const proposedVaults = useRelevantDepositOptions({
    vaults: vaultsWithPositions,
    depositOptions,
    apyRange,
    numberOfOptions: 6,
  })

  if (!address || proposedVaults.length === 0) return null

  const isPersonalizedEmpty = depositOptions != null && depositOptions.length === 0
  const shouldUseFallback = !hasIdleAssets || isPersonalizedEmpty

  return (
    <section className={cn('flex flex-col gap-3', className)}>
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5">
        <h2 className="font-medium text-heading-foreground text-lg">
          {shouldUseFallback ? 'Trending vaults' : 'Find opportunities for your idle assets'}
        </h2>
        <Link to={routes.discover} className="flex items-center gap-1 text-accent text-sm hocus:underline outline-none">
          Explore all
          <ArrowIcon size={16} />
        </Link>
      </div>
      <IdleAssetsBanner assetsBalance={assetsBalance} isFallback={shouldUseFallback} />
      <ul className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {proposedVaults.map((vault) => (
          <li key={`${vault.network.name}:${vault.vaultId}`} className="flex">
            <VaultCard vault={vault} apyRange={apyRange} includeRewards={includeRewards} className="flex-1" />
          </li>
        ))}
      </ul>
    </section>
  )
}
